import { Injectable } from '@angular/core';
import { AppService } from 'src/app/app.service';
import { HttpClient, HttpEventType, HttpParams, HttpResponse } from '@angular/common/http';
import { filter, map, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';


export interface G12Response {
  Codigo_Postal: string;
  G12: string;
  Poblacion: string;
  Provincia: string;
  direccionG12: string;
}

export interface G12AndNumberResponse {
  G17: string;
  Numero_translate: string;
}

export interface G17Response {
  G17: string;
  direccionG17: string;
  Poblacion: string;
  Provincia: string;
  Codigo_Postal: string;
}

export interface G37Response {
  Gescal37: string;
  ID_DOMICILIO_TO: string;
  direccion: string;
}

export interface InoIdsResponse {
  inoIds: any;
  gescal37: string;
  address: string;
  province: string;
  pentacomId: string;
}

@Injectable({
  providedIn: 'root'
})
export class CoverageService {

  appData: any;

  constructor(
    private httpClient: HttpClient,
    appService: AppService,
  ) {
    this.appData = appService.settings;
  }

  uploadFile(isps: string[], ino: string, file: File, progressCallback: (loaded: number, total: number) => void): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);

    const params = new HttpParams()
      .append('ino', ino)
      .append('isps', '' + isps);

    return this.httpClient.post(`${this.appData.webApi}/coverage/loadFile`, formData, {
      params,
      reportProgress: true,
      observe: 'events',
    })
      .pipe(
        tap(event => {
          if (event.type === HttpEventType.UploadProgress) {
            progressCallback(event.loaded, event.total);
          }
        }),
        filter(event => event.type === HttpEventType.Response),
        map((event: HttpResponse<any>) => event.body),
      );
  }

  getFileUploadState(): Observable<any> {
    return this.httpClient.get(`${this.appData.webApi}/coverage/getFileUploadState`)
      ;
  }

  getIndexStats(wildcard: string): Observable<any> {
    return this.httpClient.get(`${this.appData.webApi}/coverage/utils/getIndexStats/${wildcard}`)
      ;
  }

  getIndex(wildcard: string): Observable<any> {
    return this.httpClient.get(`${this.appData.webApi}/coverage/utils/getIndex/${wildcard}`)
      ;
  }

  getCurrentIndexBeingUsed(isp: string): Observable<any> {
    return this.httpClient.get(`${this.appData.webApi}/coverage/utils/getCurrentIndicesForAlias/gescal37_current_${isp}`)
      ;
  }

  configAliases(options: any): Observable<any> {
    return this.httpClient.post(`${this.appData.webApi}/coverage/utils/manageAlias`, options);
  }

  getIspLastFileLoadedInfo(query: any): Observable<any> {
    return this.httpClient.post(`${this.appData.webApi}/coverage/logs/getLastDocInfo`, query)
      ;
  }

  getIndexTempAlias(index: string, isp: string): Observable<any> {
    return this.httpClient.get(`${this.appData.webApi}/coverage/utils/getAliasForIndex/${index}/gescal37_temp_${isp}`)
      ;
  }

  refreshIndices(): Observable<any> {
    return this.httpClient.get(`${this.appData.webApi}/coverage/utils/refreshAll`)
      ;
  }

  getIspPriorities(isp: string): Observable<any> {
    return this.httpClient.get(`${this.appData.webApi}/coverage/priority/${isp}`)
      ;
  }

  createIspPriorities(isp: string, query: any): Observable<any> {
    return this.httpClient.post(`${this.appData.webApi}/coverage/priority/${isp}`, query);
  }

  updateIspPriorities(isp: string, query: any): Observable<any> {
    return this.httpClient.put(`${this.appData.webApi}/coverage/priority/${isp}`, query);
  }

  getIfAliasContainsIndices(alias: string): Observable<any> {
    return this.httpClient.get(`${this.appData.webApi}/coverage/utils/getCurrentIndicesForAlias/${alias}`)
      ;
  }

  setMaxResultWindowIno(ino: string): Observable<any> {
    const params = new HttpParams()
      .append('value', '1000000');

    return this.httpClient.put(`${this.appData.webApi}/coverage/utils/setMaxResultWindow/homeid_current_${ino}`, null, { params })
      ;
  }

  setMaxResultWindowIsp(isp: string): Observable<any> {
    const params = new HttpParams()
      .append('value', '1000000');

    return this.httpClient.put(`${this.appData.webApi}/coverage/utils/setMaxResultWindow/gescal37_current_${isp}`, null, { params })
      ;
  }

  downloadCoverage(isp: string): Observable<any> {
    return this.httpClient.get(`${this.appData.webApi}/coverage/coverage/${isp}`, { responseType: 'arraybuffer' })
      ;
  }

  deleteIspCoverageFile(isp: string): Observable<any> {
    return this.httpClient.delete(`${this.appData.webApi}/coverage/coverage/${isp}`)
      ;
  }

  generateIspCoverageFile(isp: string): Observable<any> {
    return this.httpClient.post(`${this.appData.webApi}/coverage/coverage/${isp}`, {})
      ;
  }

  getCoverageFileDownloadState(isp: string): Observable<any> {
    return this.httpClient.get(`${this.appData.webApi}/coverage/getFileDownloadState/${isp}`, {})
      ;
  }

  getPlacesByG12AndNumber(g12: string, isp: string, streetNumber?: number): Observable<G12AndNumberResponse[]> {
    return this.httpClient.post<G12AndNumberResponse[]>(`${this.appData.webApi}/coverage/coverage/getPlacesByG12AndNumber`, {
      G12: g12,
      Numero_translate: streetNumber ? `*${streetNumber}*` : '*',
      isp,
    });
  }

  getPlacesByG12(street: string, isp: string): Observable<G12Response[]> {
    return this.httpClient.post<G12Response[]>(`${this.appData.webApi}/coverage/coverage/getPlacesByG12`, {
      direccionSinProvG12: street,
      isp,
    });
  }

  getPlacesByG17(street: string, isp: string): Observable<G17Response[]> {
    return this.httpClient.post<G17Response[]>(`${this.appData.webApi}/coverage/coverage/getPlacesByG17`, {
      direccionSinProvG17: street,
      isp,
    }
    );
  }

  getG37PlacesBySelectedG17(g17: string, isp: string): Observable<G37Response[]> {
    return this.httpClient.post<G37Response[]>(`${this.appData.webApi}/coverage/coverage/getG37PlacesBySelectedG17`, {
      G17: g17,
      isp,
    });
  }

  getInoIdsFromIdAndIsp(id: string, isp: string): Observable<InoIdsResponse> {
    return this.httpClient.post<InoIdsResponse>(`${this.appData.webApi}/coverage/homeId/getInoIdsFromIdAndIsp`, {
      id,
      isp,
    });
  }

}
